import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Group, List, NumberInput, SimpleGrid, Stack, Switch, Text, Textarea, ThemeIcon, Title } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconCheck, IconCopy, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { checkBallot, issuesToFormErrors, teamOnSide, type Ballot, type Round, type SideIndex, type SpeakerNote } from '@flow/core';
import { useAiAvailable, useSummarizeRound } from '../ai/useAi';
import { BallotAssistant } from '../ai/BallotAssistant';
import { run, useDeps, useUseCases } from '../../app/services';
import { copyText } from '../../infra/browser';
import { Term } from '../knowledge/KnowledgeDrawer';

const RFD_OUTLINE = `I voted for ___ because ___.

The key argument was ___. It outweighed ___ because ___.

What would have changed my mind: ___.
`;

const Copy = ({ text, label, testId }: { text: string; label: string; testId?: string }) => (
  <Button
    size="xs"
    leftSection={<IconCopy size={14} />}
    disabled={!text.trim()}
    data-testid={testId}
    onClick={async () => {
      const ok = await copyText(text);
      notifications.show({ message: ok ? `${label} copied. Paste it into Tabroom.` : 'Copy failed. Select the text and copy it manually.', color: ok ? 'green' : 'red', autoClose: 1800 });
    }}
  >
    Copy {label}
  </Button>
);

/** Write the ballot after committing. Text is yours; checks nudge toward constructive feedback. */
/** Your private notes on a speaker (from the flow), with a button to copy them into the comment. */
const YourNotes = ({ note, onCopy }: { note: SpeakerNote | undefined; onCopy: (text: string) => void }) => {
  if (!note || (!note.text.trim() && note.tags.length === 0)) return null;
  const text = [note.tags.length ? note.tags.join(', ') : '', note.text.trim()].filter(Boolean).join('. ');
  return (
    <Box mt={4} p={6} style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 4 }} data-testid="your-notes">
      <Group justify="space-between" gap={4} wrap="nowrap" align="flex-start">
        <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}><b>Your notes:</b> {text}</Text>
        <Button size="compact-xs" variant="subtle" onClick={() => onCopy(text)}>Copy in</Button>
      </Group>
    </Box>
  );
};

export const BallotPanel = ({ round, goDecide }: { round: Round; goDecide: () => void }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const ai = useAiAvailable();
  const summarize = useSummarizeRound();
  const ballot = useLiveQuery(() => deps.ballots.get(round.id), [round.id]);
  const names = useLiveQuery(() => deps.names.get(round.id), [round.id]);
  const [draft, setDraft] = useState<Pick<Ballot, 'rfd' | 'teamComments' | 'speakerComments' | 'points'> | null>(null);
  const [pointErrors, setPointErrors] = useState<Record<string, string>>({});
  const dirty = useRef(false);

  useEffect(() => {
    if (ballot && !draft) setDraft({ rfd: ballot.rfd, teamComments: ballot.teamComments, speakerComments: ballot.speakerComments, points: ballot.points });
  }, [ballot, draft]);

  // Autosave the draft shortly after typing stops.
  useEffect(() => {
    if (!draft || !dirty.current) return;
    const id = setTimeout(async () => {
      dirty.current = false;
      // Text and points save separately so a bad point value never blocks saving your writing.
      const text = await uc.saveBallot(round.id, { rfd: draft.rfd, teamComments: draft.teamComments, speakerComments: draft.speakerComments });
      if (!text.ok) notifications.show({ color: 'red', message: text.error.message });
      const r = await uc.saveBallot(round.id, { points: draft.points });
      if (!r.ok && r.error.kind === 'validation') setPointErrors(issuesToFormErrors(r.error.issues));
      else setPointErrors({});
    }, 500);
    return () => clearTimeout(id);
  }, [draft, round.id, uc]);

  // Don't lose the last half-second of typing when switching tabs.
  const latest = useRef(draft);
  latest.current = draft;
  useEffect(() => () => {
    if (dirty.current && latest.current) {
      const d = latest.current;
      void uc.saveBallot(round.id, { rfd: d.rfd, teamComments: d.teamComments, speakerComments: d.speakerComments });
      void uc.saveBallot(round.id, { points: d.points });
    }
  }, [round.id, uc]);

  const update = (patch: Partial<NonNullable<typeof draft>>) => {
    dirty.current = true;
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const f = round.format;
  const winner = ballot?.winnerSide ?? null;
  const loser = winner === null ? null : ((1 - winner) as SideIndex);
  const teamCode = (s: SideIndex | null) => {
    if (s === null) return null;
    const t = teamOnSide(round, s);
    return t === 'A' ? round.teamA.code : t === 'B' ? round.teamB.code : null;
  };
  const speakers = useMemo(
    () => (['A', 'B'] as const).flatMap((team) => Array.from({ length: f.teamSize }, (_, i) => {
      const name = (team === 'A' ? names?.teamA : names?.teamB)?.[i]?.trim();
      const code = team === 'A' ? round.teamA.code : round.teamB.code;
      return { key: `${team}${i}`, team, label: name || `${code} speaker ${i + 1}` };
    })),
    [names, round, f.teamSize],
  );

  if (ballot === undefined || draft === null) {
    if (ballot === undefined) {
      return (
        <Box p="md" maw={720} mx="auto">
          <Text mb="sm">Decide first, then write the ballot.</Text>
          <Button variant="filled" onClick={goDecide}>Go to Decide →</Button>
        </Box>
      );
    }
    return null;
  }

  if (ballot.committedAt === null) {
    return (
      <Box p="md" maw={720} mx="auto">
        <Alert title="Decide first" mb="md">Commit your decision in the Decide tab before writing the ballot. That way the ballot explains your decision instead of shaping it.</Alert>
        <Button variant="filled" onClick={goDecide}>Go to Decide →</Button>
      </Box>
    );
  }

  const checks = checkBallot({
    rfd: draft.rfd,
    winnerLabel: winner === null ? null : f.sideLabels[winner],
    winnerCode: teamCode(winner),
    loserLabel: loser === null ? null : f.sideLabels[loser],
    teamComments: [draft.teamComments.A, draft.teamComments.B],
    speakerComments: Object.values(draft.speakerComments),
  });
  const pts = speakers.map((s) => draft.points[s.key]).filter((p): p is number => typeof p === 'number');
  const teamTotal = (team: 'A' | 'B') => speakers.filter((s) => s.team === team).reduce((acc, s) => acc + (draft.points[s.key] ?? 0), 0);
  const winnerTeam = winner === null ? null : teamOnSide(round, winner);
  const lowPointWin = winnerTeam && pts.length === speakers.length && teamTotal(winnerTeam) < teamTotal(winnerTeam === 'A' ? 'B' : 'A');
  const allComments = speakers.map((s) => `${s.label}: ${draft.speakerComments[s.key] ?? ''}`).join('\n\n');

  return (
    <Box p="md" maw={960} mx="auto">
      <Group justify="space-between" mb="sm">
        <Title order={3} size="h5">Ballot: {winner !== null ? `${f.sideLabels[winner]}${teamCode(winner) ? ` (${teamCode(winner)})` : ''} wins` : ''}</Title>
        <Switch
          label="Submitted in Tabroom"
          checked={ballot.submittedAt !== null}
          onChange={async (e) => {
            const submitted = e.currentTarget.checked;
            const ok = await run(uc.markSubmitted(round.id, submitted));
            // The recap is written only now, after the decision is final (and while debater names can still be redacted).
            if (ok && submitted && ai && !round.summary) void summarize(round.id);
          }}
          data-testid="submitted"
        />
      </Group>

      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
        <Stack gap="md" style={{ gridColumn: 'span 2' }}>
          {ballot.roundNotes?.trim() && (
            <Box p={6} style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 4 }} data-testid="round-notes-ref">
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}><b>Your round notes:</b> {ballot.roundNotes}</Text>
            </Box>
          )}
          <Box>
            <Group justify="space-between" mb={4}>
              <Text fw={600} size="sm"><Term q="RFD">Reason for decision</Term></Text>
              <Group gap={4}>
                {!draft.rfd.trim() && <Button size="xs" variant="subtle" onClick={() => update({ rfd: RFD_OUTLINE })}>Insert outline</Button>}
                <Copy text={draft.rfd} label="RFD" testId="copy-rfd" />
              </Group>
            </Group>
            <Textarea classNames={{ input: 'writing' }} autosize minRows={6} value={draft.rfd} onChange={(e) => update({ rfd: e.currentTarget.value })} placeholder="In your own words: who won, the argument that decided it, why it outweighed, and what the losing team could do next time." data-testid="rfd" />
            <Text size="xs" c="dimmed" ta="right">{draft.rfd.length} characters</Text>
          </Box>

          {(['A', 'B'] as const).map((team) => (
            <Box key={team}>
              <Group justify="space-between" mb={4}>
                <Text fw={600} size="sm">Comments to {team === 'A' ? round.teamA.code : round.teamB.code}</Text>
                <Copy text={draft.teamComments[team]} label={`${team === 'A' ? round.teamA.code : round.teamB.code} comments`} />
              </Group>
              <Textarea classNames={{ input: 'writing' }} autosize minRows={3} value={draft.teamComments[team]} onChange={(e) => update({ teamComments: { ...draft.teamComments, [team]: e.currentTarget.value } })} placeholder="One thing they did well, one concrete thing to work on." />
            </Box>
          ))}

          <Box>
            <Group justify="space-between" mb={4}>
              <Text fw={600} size="sm">Speakers</Text>
              <Copy text={allComments} label="speaker comments" />
            </Group>
            <Stack gap="xs">
              {speakers.map((s) => (
                <Card key={s.key} className="ruled" padding="xs">
                  <Group align="flex-start" wrap="nowrap">
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={600} mb={4}>{s.label}</Text>
                      <Textarea classNames={{ input: 'writing' }} autosize minRows={2} value={draft.speakerComments[s.key] ?? ''} onChange={(e) => update({ speakerComments: { ...draft.speakerComments, [s.key]: e.currentTarget.value } })} placeholder="Strength · next step" />
                      <YourNotes
                        note={ballot.speakerNotes?.[s.key]}
                        onCopy={(text) => {
                          const cur = draft.speakerComments[s.key] ?? '';
                          update({ speakerComments: { ...draft.speakerComments, [s.key]: cur.trim() ? `${cur.trimEnd()}\n${text}` : text } });
                        }}
                      />
                    </Box>
                    <NumberInput
                      w={100}
                      label={<Term q="Speaker points">Points</Term>}
                      min={f.points.min}
                      max={f.points.max}
                      step={f.points.step}
                      decimalScale={1}
                      clampBehavior="none"
                      value={draft.points[s.key] ?? ''}
                      onChange={(v) => update({ points: { ...draft.points, [s.key]: typeof v === 'number' ? v : null } })}
                      error={pointErrors[`points.${s.key}`]}
                      data-testid={`points-${s.key}`}
                    />
                  </Group>
                </Card>
              ))}
            </Stack>
          </Box>
        </Stack>

        <Stack gap="md" style={{ position: 'sticky', top: 8, alignSelf: 'start' }}>
          <BallotAssistant
            round={round}
            ballot={ballot}
            fields={{ rfd: draft.rfd, teamComments: draft.teamComments, speakerComments: draft.speakerComments, points: draft.points }}
            onEdits={(e) => {
              const next = {
                ...(e.rfd !== undefined ? { rfd: e.rfd } : {}),
                ...(e.teamComments ? { teamComments: { ...draft.teamComments, ...e.teamComments } } : {}),
                ...(e.speakerComments ? { speakerComments: { ...draft.speakerComments, ...e.speakerComments } } : {}),
              };
              if (Object.keys(next).length) update(next);
            }}
          />
          <Card className="ruled" padding="sm">
            <Text fw={600} size="sm" mb="xs">Quick checks</Text>
            <List spacing={6} size="sm" data-testid="ballot-checks">
              {checks.map((c) => (
                <List.Item key={c.id} icon={<ThemeIcon size={16} radius="xl" color={c.ok ? 'green' : 'gray'} variant={c.ok ? 'filled' : 'light'}>{c.ok ? <IconCheck size={10} /> : <IconX size={10} />}</ThemeIcon>}>
                  <Text size="sm" c={c.ok ? undefined : 'dimmed'}>{c.label}</Text>
                </List.Item>
              ))}
            </List>
            {lowPointWin && <Alert color="yellow" mt="sm" p="xs"><Text size="xs"><Term q="LPW">Low-point win</Term>: the winner has fewer total points. Some tournaments don't allow this. Double-check.</Text></Alert>}
            <Text size="xs" c="dimmed" mt="sm">Instant, on this device, no AI. For more, ask the assistant for a critique.</Text>
          </Card>
        </Stack>
      </SimpleGrid>
    </Box>
  );
};
